<script setup lang="ts">
import DeleteButton from '@/components/Buttons/DeleteButton.vue'
import { BaseCard } from '@/components/Cards'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import { TypographyItem } from '@/components/Typography'
import TitleTypography from '@/components/Typography/TitleTypography.vue'
import DetectionWrapper from '@/components/Wrappers/DetectionWrapper.vue'
import type { DropZoneData } from '@/types/components/DropZoneData'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import { type DeviceModel } from '@/types/models/DeviceModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import { type Position } from '@vueuse/core'
import { computed, inject, type ComputedRef } from 'vue'
import IconItem from '../../../../../components/Icon/IconItem.vue'
import TooltipItem from '../../../../../components/Tooltip/TooltipItem.vue'
import { EHubContext } from '../../../../../types/context/HubContextEnum'
import { EDeviceStatus, EDeviceStatusColors } from '../../../../../types/enum/DeviceStatusEnum'
import CheckpointClearDetectionsAction from './CheckpointClearDetectionsAction.vue'
import DeviceCheckpointCard from './DeviceCheckpointCard.vue'
import DeviceDropZone from './DeviceDropZone.vue'
import DeviceUnavailabilityTooltip from './DeviceUnavailabilityTooltip.vue'

type Props = {
    sync?: boolean
    data: CheckpointModel
    dropZoneData?: DropZoneData
    detections?: DetectionModel[]
    deletePermission?: PermissionsEnums
    editPermission?: PermissionsEnums
}
const props = defineProps<Props>()
type Emits = {
    (e: 'delete', checkpoint: CheckpointModel): void
    (e: 'click', checkpoint: CheckpointModel | undefined, event: MouseEvent): void
    (e: 'dragStart', device?: Partial<DeviceModel>, checkpoint?: CheckpointModel): void
    (e: 'dragEnd', position?: Position): void
    (e: 'close'): void
    (e: 'refresh'): void
}
defineEmits<Emits>()

const isViewerOnly = inject<boolean>(EHubContext.IS_VIEWER_ONLY, false)

const devices = inject<ComputedRef<Partial<DeviceModel[]>>>('devices')

const associatedDevices = computed(
    () =>
        props.data?.associates?.map(item => ({
            ...item,
            ...((devices?.value ?? [])?.find(
                deviceItem =>
                    (deviceItem?.device_id ?? deviceItem?.id) === (item.device_id ?? item.id)
            ) ?? {})
        })) ?? []
)
</script>

<template>
    <DeviceDropZone
        :checkpoint="data"
        :dropZoneData="dropZoneData"
        @close="$emit('close')"
        @refresh="$emit('refresh')"
        v-slot="props">
        <BaseCard
            class="flex primary-border color-text full-width checkpoint-card pointer"
            @click="$emit('click', data, $event)"
            v-bind="props">
            <template #title>
                <TitleTypography
                    :title="data.name"
                    :subtitle="
                        data?.partner_config?.id && Array.isArray(data?.partner_config?.race)
                            ? `${$t('checkpoint.partner.livetrailCheckpointId')} : ${
                                  data?.partner_config?.id
                              }`
                            : undefined
                    " />
                <CheckpointClearDetectionsAction
                    v-if="!isViewerOnly"
                    :data="data"
                    asIcon
                    @refresh="$emit('refresh')" />
            </template>
            <template #content>
                <StackLayout direction="column" :gap="8" isResponsiveSmallScreen>
                    <StackLayout v-if="associatedDevices?.length" :gap="4" class="flex-wrap">
                        <StackLayout
                            v-for="(device, key) in associatedDevices"
                            :key="key"
                            direction="column"
                            :gap="4"
                            class="full-width checkpoint-device-card"
                            isResponsiveSmallScreen>
                            <BaseCard
                                v-if="isViewerOnly"
                                ref="handle"
                                :class="`full-width primary-border device-card cursor-default ${
                                    device?.unavailability_periods?.fully_available
                                        ? ''
                                        : 'rented-device'
                                }`">
                                <template #content>
                                    <TooltipItem
                                        autoPosition
                                        :disableTooltip="
                                            device?.unavailability_periods?.fully_available
                                        ">
                                        <StackLayout class="space-between full-width">
                                            <TypographyItem
                                                v-if="device.device_id || device.id"
                                                :label="device.name" />
                                            <IconItem
                                                icon="circle"
                                                :type="
                                                    EDeviceStatusColors[
                                                        (device.status as EDeviceStatus) ??
                                                            EDeviceStatus.OFFLINE
                                                    ]
                                                " />
                                        </StackLayout>
                                        <template
                                            v-if="device?.unavailability_periods?.periods"
                                            #content>
                                            <DeviceUnavailabilityTooltip
                                                :periods="
                                                    device?.unavailability_periods?.periods
                                                " />
                                        </template>
                                    </TooltipItem>
                                </template>
                            </BaseCard>
                            <TooltipItem
                                autoPosition
                                :disableTooltip="device?.unavailability_periods?.fully_available"
                                v-else>
                                <DeviceCheckpointCard
                                    :device="device"
                                    @dragStart="$emit('dragStart', $event, data)"
                                    @dragEnd="$emit('dragEnd', $event)"
                                    :class="`full-width ${
                                        device?.unavailability_periods?.fully_available
                                            ? ''
                                            : 'rented-device'
                                    }`" />
                                <template v-if="device?.unavailability_periods?.periods" #content>
                                    <DeviceUnavailabilityTooltip
                                        :periods="device?.unavailability_periods?.periods" />
                                </template>
                            </TooltipItem>
                        </StackLayout>
                    </StackLayout>
                    <TypographyItem v-else :label="$t('event.checkpoint.noDeviceAssociated')" />
                    <StackLayout direction="column" class="device-card-footer">
                        <DetectionWrapper :detections="detections ?? []" class="detection-class" />
                    </StackLayout>
                </StackLayout>
            </template>
            <template v-if="!sync && !isViewerOnly" #footer>
                <StackLayout class="full-width">
                    <PermissionWrapper :permission="deletePermission">
                        <DeleteButton
                            class="full-width left-button"
                            @click="$emit('delete', data)" />
                    </PermissionWrapper>
                </StackLayout>
            </template>
        </BaseCard>
    </DeviceDropZone>
</template>

<style scoped lang="scss">
.checkpoint-card {
    flex-direction: column;

    & :deep(.card-header-title) {
        justify-content: space-between;
        & .title {
            font-size: large;
            color: var(--color-text);
        }

        & .subtitle {
            color: var(--color-text-secondary);
        }
    }

    & :deep(.card-content) {
        height: 100%;

        & hr {
            margin-left: unset;
            margin-right: unset;
            border-style: unset;
        }
    }

    & :deep(.card-footer) {
        flex: auto;
    }

    & :deep(.card-header-title),
    & :deep(.card-content) {
        padding: 0.5em;
    }
}
.device-card {
    flex-direction: column;
    cursor: pointer;
    pointer-events: all;

    &.rented-device {
        background: repeating-linear-gradient(
            135deg,
            #fb000000,
            transparent 10px,
            var(--v-theme-error) 0px,
            var(--v-theme-error) 15px
        );
    }
}

.checkpoint-device-card {
    cursor: default;
}

.detection-class {
    & :deep(.no-detection) {
        white-space: nowrap;
    }
}
</style>
